import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '@/api/services';
import { AccountOperationType, InterfaceResponseEnum } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import naLogo from '@/assets/Nautic Alert Logo.png';

const INVALID_CHARS = /[\s/\\'"\t]/;

function mapResponseError(responseEnum: InterfaceResponseEnum | number | undefined): string {
  switch (Number(responseEnum)) {
    case InterfaceResponseEnum.NOT_FOUND:
      return 'Account not found. Please check your username.';
    case InterfaceResponseEnum.VERIFICATION_CODE_INVALID:
      return 'Incorrect verification code.';
    case InterfaceResponseEnum.VERIFICATION_LIMIT:
      return 'Maximum verification attempts reached. Please start over.';
    case InterfaceResponseEnum.TIMEOUT:
      return 'Verification code has expired. Please request a new code.';
    case InterfaceResponseEnum.PASSWORD_INVALID:
      return 'Password does not meet requirements (6-29 characters, no spaces or special characters).';
    case InterfaceResponseEnum.USER_ID_INVALID:
      return 'Invalid username format.';
    case InterfaceResponseEnum.SERVER_ERROR:
    case InterfaceResponseEnum.DB_ERROR:
    case InterfaceResponseEnum.DB_WRITE_ERROR:
      return 'A server error occurred. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  // 1: Username · 2: Code + Password (mirrors the mobile app's single
  // VerificationPasswordResetPage) · 3: Success
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [username, setUsername] = useState('');

  // Step 2 — all three inputs are on the same screen, exactly like the
  // mobile VerificationPasswordResetPageViewModel.cs.
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);

  // Shared
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500 dark:focus:border-teal-500';
  const labelClass =
    'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

  const handleStep1Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('Please enter your username.');
      return;
    }

    setIsLoading(true);
    try {
      const resp = await authService.accountOperation({
        operationType: AccountOperationType.PRE_CHANGE_PASSWORD_VERIFY,
        userName: username.trim(),
      });

      const flat = resp as unknown as { httpStatusCode: number; responseEnum: number };
      const statusCode = resp.responseObj?.httpStatusCode ?? flat.httpStatusCode ?? 400;
      const enumVal = resp.responseObj?.responseEnum ?? flat.responseEnum;

      if (statusCode === 200) {
        setStep(2);
        setAttemptsRemaining(3);
        setVerificationCode('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(mapResponseError(enumVal));
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Derived validity. Mirrors the mobile validateInputs() (all non-empty +
  // passwords match) plus the inline format hints we agreed on earlier; the
  // server is still the authority on code correctness and password rules.
  const isCodeFormatValid = /^\d{5}$/.test(verificationCode);
  const isNewPasswordFormatValid =
    newPassword.length >= 6 && newPassword.length <= 29 && !INVALID_CHARS.test(newPassword);
  const passwordsMatch = confirmPassword.length > 0 && confirmPassword === newPassword;
  const canSubmitStep2 =
    !isLoading && isCodeFormatValid && isNewPasswordFormatValid && passwordsMatch;

  // Step 2 submit: single round-trip with code + password, exactly like
  // VerificationPasswordResetPageViewModel.changePasswordPressedCallback in
  // the mobile app. Dispatch per response enum the same way mobile does.
  const handleStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isCodeFormatValid) {
      setError('Please enter a 5-digit verification code.');
      return;
    }
    if (!isNewPasswordFormatValid) {
      setError('Password must be 6-29 characters with no spaces, slashes, tabs, or quotes.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const resp = await authService.accountOperation({
        operationType: AccountOperationType.POST_CHANGE_PASSWORD_VERIFY,
        userName: username.trim(),
        newPassword,
        verificationCode,
      });

      const flat = resp as unknown as { httpStatusCode: number; responseEnum: number };
      const statusCode = resp.responseObj?.httpStatusCode ?? flat.httpStatusCode ?? 400;
      const enumVal = resp.responseObj?.responseEnum ?? flat.responseEnum;

      if (statusCode === 200) {
        setStep(3);
        return;
      }

      const enumNum = Number(enumVal);
      // Mirror mobile changePasswordPressedCallback dispatch:
      //  - VERIFICATION_CODE_INVALID -> "incorrect", stay on this page.
      //  - VERIFICATION_LIMIT/TIMEOUT/NOT_FOUND -> "expired"/"max attempts",
      //    go back a step (Shell.Current.GoToAsync("../") in mobile = step 1).
      //  - PASSWORD_INVALID -> password complaint, stay.
      //  - else -> generic "unexpected".
      if (enumNum === InterfaceResponseEnum.VERIFICATION_CODE_INVALID) {
        setAttemptsRemaining((prev) => Math.max(0, prev - 1));
      } else if (
        enumNum === InterfaceResponseEnum.VERIFICATION_LIMIT ||
        enumNum === InterfaceResponseEnum.TIMEOUT ||
        enumNum === InterfaceResponseEnum.NOT_FOUND
      ) {
        setError(mapResponseError(enumVal));
        // Send the user back to step 1 to restart with a fresh code.
        setStep(1);
        setVerificationCode('');
        setNewPassword('');
        setConfirmPassword('');
        setAttemptsRemaining(3);
        return;
      }
      setError(mapResponseError(enumVal));
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-navy-950">
      <div className="w-full max-w-md px-4">
        {/* Logo & Brand */}
        <div className="mb-8 text-center">
          <img src={naLogo} alt="Nautic Alert" className="mx-auto mb-4 h-16 object-contain" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Vessel Monitoring Portal</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-lg dark:border-navy-700 dark:bg-navy-900">
          {/* Step indicator — 1: Username · 2: Code + Password · 3: Success
              (mirrors the mobile flow: username page -> single Verification
              Password Reset page -> back to login on success). */}
          <div className="mb-6 flex items-center justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                    step >= s
                      ? 'bg-teal-500 text-white'
                      : 'bg-slate-200 text-slate-500 dark:bg-navy-700 dark:text-slate-400'
                  }`}
                >
                  {step > s ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    s
                  )}
                </div>
                {s < 3 && (
                  <div
                    className={`h-0.5 w-8 ${
                      step > s ? 'bg-teal-500' : 'bg-slate-200 dark:bg-navy-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Enter Username */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-5">
              <h2 className="text-center text-lg font-semibold text-slate-800 dark:text-white">
                Enter Your Username
              </h2>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="fp-username" className={labelClass}>
                  Username
                </label>
                <input
                  id="fp-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={inputClass}
                  placeholder="Enter your username"
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading || !username.trim()}
                loading={isLoading}
                className="w-full"
              >
                Send Verification Code
              </Button>
            </form>
          )}

          {/* Step 2: Code + New Password (mirrors mobile
              VerificationPasswordResetPage — one screen, one submit). */}
          {step === 2 && (
            <form onSubmit={handleStep2Submit} className="space-y-4">
              <h2 className="text-center text-lg font-semibold text-slate-800 dark:text-white">
                Reset Password
              </h2>

              <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-400">
                If your username exists, a verification code has been sent to your phone and email on file. The code expires in 10 minutes.
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="fp-code" className={labelClass}>
                  Verification Code
                </label>
                <input
                  id="fp-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 5))
                  }
                  className={`${inputClass} text-center text-lg tracking-widest`}
                  placeholder="00000"
                  autoFocus
                />
              </div>

              <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                Attempts remaining:{' '}
                <span className={`font-semibold ${attemptsRemaining <= 1 ? 'text-red-500' : 'text-slate-700 dark:text-slate-300'}`}>
                  {attemptsRemaining} of 3
                </span>
              </p>

              <div>
                <label htmlFor="fp-new-password" className={labelClass}>
                  New Password
                </label>
                <input
                  id="fp-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={inputClass}
                  placeholder="6-29 characters"
                  autoComplete="new-password"
                />
                {newPassword.length > 0 && !isNewPasswordFormatValid && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    6–29 characters, no spaces, slashes, tabs, or quotes.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="fp-confirm-password" className={labelClass}>
                  Confirm Password
                </label>
                <input
                  id="fp-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    Passwords do not match.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!canSubmitStep2}
                loading={isLoading}
                className="w-full"
              >
                Reset Password
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setError('');
                  setVerificationCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="w-full text-center text-sm text-teal-600 hover:text-teal-500 dark:text-teal-400 dark:hover:text-teal-300"
              >
                Request New Code
              </button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <svg
                  className="h-7 w-7 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                Password Reset Successfully
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Sign In
              </Button>
            </div>
          )}

          {/* Back to Sign In */}
          {step !== 3 && (
            <div className="mt-6 border-t border-slate-200 pt-6 text-center dark:border-navy-700">
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
              >
                Back to Sign In
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          &copy; {new Date().getFullYear()} Nautic Alert. All rights reserved.
        </p>
      </div>
    </div>
  );
}
